import { combineReducers } from "@reduxjs/toolkit";
import { History } from "history";
import { connectRouter } from "connected-react-router";
import appSettingReducer from "modules/appSetting/redux/appSettingSlice";
import announcementReducer from "modules/announcement/redux/announcementSlice";
import spaceReducer from "modules/space/redux";
import eventReducer from "modules/event/eventSlice";
import { usersInEventReducer } from "modules/event/usersInEvent/reducer";
import userRoomStatusReducer from "modules/room/redux/roomSlice";
import mapNavigationReducer from "modules/mapNavigation/redux/mapNavigationSlice";
import { permissionsAssistanceDialogReducer } from "modules/permissionAssistance/redux";
import deviceInfoReducer from "modules/deviceInfo";
import authReducer from "modules/auth/redux/authSlice";
import networkReducer from "modules/network/redux/reducer";
import helpChatReducer from "modules/helpChat/reducer";
import notificationReducer from "modules/notification/redux/notificationSlice";
import dialogNotificationsReducer from "modules/dialogNotification/redux/dialogNotificationSlice";
import companyReducer from "modules/company/redux/slice";
import companySettingsReducer from "modules/companySettings/redux/reducer";
import userProfileCardReducer from "modules/userProfileCard/redux/reducer";
import chatReducer from "modules/chat/redux/reducer";
import broadcastReducer from "modules/broadcast/redux/reducer";
import broadcasterReducer from "modules/broadcaster/redux/reducer";
import broadcastQAReducer from "modules/qa/redux/reducer";
import broadcastRaiseHandReducer from "modules/broadcastRaiseHand/redux/broadcastRaiseHandSlice";
import broadcastRecordingReducer from "modules/broadcastRecording/redux/reducer";
import broadcastWhiteboardReducer from "modules/broadcastWhiteboard/redux/slice";
import broadcastFocusScreenReducer from "modules/broadcastFocusScreen/redux/broadcastFocusScreenSlice";
import broadcastFocusScreenNotificationReducer from "modules/broadcastFocusScreenNotification/redux/reducer";
import streamingReducer from "modules/streaming/redux/streamingSlice";
import presenceReducer from "modules/eventUserPresence/redux/reducer";
import roomWhiteboardReducer from "modules/roomWhiteboard/redux/slice";
import userStatusReducer from "modules/userStatus/redux/reducer";
import userStateReducer from "modules/userState/redux/reducer";
import userPreferencesReducer from "modules/userPreferences/redux/reducer";
import timerReducer from "modules/timer/redux/slice";
import theaterReducer from "modules/theater/theaterSlice";
import userProfileWindowReducer from "modules/userProfileWindow/redux/userProfileWindowSlice";
import showCamAndMicPopoverReducer from "modules/onboarding/redux/reducer";
import audienceViewReducer from "modules/broadcast/AudienceView/reducer";
import serverTimeReducer from "modules/serverTime/redux/reducer";
import couponReducer from "modules/company/companyCoupon/companyCouponSlice";
import speechDetectorReducer from "modules/speechDetection/redux/speechDetectorSlice";
import eventLandingReducer from "modules/eventLanding/redux/eventLandingSlice";
import systemCheckReducer from "modules/micCamCheck/systemCheck/redux/systemCheckSlice";
import eventDirectoryReducer from "modules/eventDirectory/redux/slice";
import { splitReducer } from "@splitsoftware/splitio-redux";
import onlinePresenceReducer from "modules/presence/presence.slice";
import transcriptionSlice from "modules/transcription/redux/transcriptionSlice";
import appVersionReducer from "modules/version/appVersion.slice";
import cameraSettingsReducer from "modules/cameraSettings/redux/reducers";
import shuffleAttendeesReducer from "modules/shuffleAttendees/redux/reducers";
import lobbyReducer from "modules/lobby/redux/slice";
import userProblemReducer from "modules/userProblemReporter/redux/reducer";
import { assignedTableNudgeSlice } from "modules/assignTables/redux";
import { enforceTableAssignmentSlice } from "modules/enforceTableAssignment/redux";
import { rightPanelSlice } from "modules/rightPanel/redux";
import sideFeatureMenuReducer from "modules/eventButtons/redux";
import { embedContentSlice } from "modules/eventEmbedContent/redux";
import { eventContentSlice } from "modules/eventContent/redux";
import directTableLinkReducer from "modules/conversation/hooks/useCopyTableLink/slice";
import roomsReducer from "./rooms/roomsSlice";

const createRootReducer = (history: History) =>
  combineReducers({
    appVersion: appVersionReducer,
    announcement: announcementReducer,
    appSetting: appSettingReducer,
    assignedTableNudge: assignedTableNudgeSlice,
    audienceView: audienceViewReducer,
    auth: authReducer,
    // broadcast modules
    broadcast: broadcastReducer,
    broadcastFocusScreen: broadcastFocusScreenReducer,
    broadcastFocusScreenNotification: broadcastFocusScreenNotificationReducer,
    broadcastQA: broadcastQAReducer,
    broadcastRaiseHand: broadcastRaiseHandReducer,
    broadcastRecording: broadcastRecordingReducer,
    broadcastWhiteboard: broadcastWhiteboardReducer,
    broadcaster: broadcasterReducer,
    cameraSettings: cameraSettingsReducer,
    chatV2: chatReducer,
    company: companyReducer,
    companySettings: companySettingsReducer,
    companyCoupon: couponReducer,
    deviceInfo: deviceInfoReducer,
    dialogNotifications: dialogNotificationsReducer,
    embedContent: embedContentSlice,
    enforceTableAssignment: enforceTableAssignmentSlice,
    event: eventReducer,
    eventContent: eventContentSlice,
    eventLanding: eventLandingReducer,
    eventDirectory: eventDirectoryReducer,
    helpChatEvent: helpChatReducer,
    lobby: lobbyReducer,
    mapNavigation: mapNavigationReducer,
    network: networkReducer,
    notification: notificationReducer,
    permissionsAssistanceDialog: permissionsAssistanceDialogReducer,
    presence: presenceReducer,
    rightPanel: rightPanelSlice,
    // room whiteboard
    roomWhiteboard: roomWhiteboardReducer,
    rooms: roomsReducer,
    router: connectRouter(history),
    serverTime: serverTimeReducer,
    showCamAndMicPopover: showCamAndMicPopoverReducer,
    shuffleAttendees: shuffleAttendeesReducer,
    space: spaceReducer,
    speechDetector: speechDetectorReducer,
    splitio: splitReducer,
    streaming: streamingReducer,
    systemCheck: systemCheckReducer,
    theater: theaterReducer,
    timer: timerReducer,
    transcription: transcriptionSlice,
    userPreferences: userPreferencesReducer,
    userProblem: userProblemReducer,
    // user profile
    userProfileCard: userProfileCardReducer,
    userProfileWindow: userProfileWindowReducer,
    userRoomStatus: userRoomStatusReducer,
    userState: userStateReducer,
    userStatus: userStatusReducer,
    usersInEvent: usersInEventReducer,
    onlinePresence: onlinePresenceReducer,
    sideFeatureMenu: sideFeatureMenuReducer,
    directTableLink: directTableLinkReducer,
  });

export default createRootReducer;
